// 検索条件に基づいて orders コレクションを購読するフック。
//
// 旧実装は「コレクション全件を onSnapshot で購読して変更検知 → 変更のたびに
// getDocs で条件付き再取得」という二重読みだった(初回スナップショットで全 doc の
// 読み取りが発生し、どこかの1件が変わるだけで対象範囲を丸ごと再読していた)。
//
// 本フックは選択中の日付種類ごとの範囲クエリ(最大4本、通常1本)を直接購読する。
//  ・初回もその範囲の doc しか読まない
//  ・以後は変更のあった doc の差分だけが届く(リアルタイム反映は従来どおり)
//  ・範囲条件は単一フィールドのみなので複合インデックスが不要
//  ・状態/部署/myOwn の絞り込みとソートはクライアント側(applyOrderSearch)のため、
//    これらの変更では再購読も Firestore の読み取りも発生しない
//
// 日付条件の変更時は購読を張り替えるが、既存データは消さない。
// applyOrderSearch が日付範囲もクライアント側で再検証するため、新しい範囲の
// 初回スナップショットが届くまでの間も「新旧範囲の共通部分」だけが表示され、
// 画面が一瞬空になったり範囲外の行が見えたりしない。
import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db, FirebaseTimestamp } from "../firebase";
import { applyOrderSearch } from "../functions/order/applyOrderSearch";
import { dateKindToField } from "../functions/order/orderSearchModel";

export const useOrdersSubscription = ({
  conditions,
  loginUserEmail,
  enabled = true,
}) => {
  // 日付種類ごとのスナップショット(kindId → Map<docId, docData>)
  const [docsByKind, setDocsByKind] = useState({});
  // 購読開始後に初回スナップショットを受け取った種類(ローディング判定用)
  const [loadedKinds, setLoadedKinds] = useState({});
  // エラーは種類ごとに持ち、その種類の成功スナップショットで解消する
  // (単一の error だと、一度失敗すると再購読まで貼り付いたままになる)
  const [errorsByKind, setErrorsByKind] = useState({});

  const { from, to } = conditions;
  // 購読の張り替えは日付条件が変わったときだけに抑える(プリミティブで比較する)
  const dateKindsKey = conditions.dateKinds.join(",");
  const fromMs = new Date(
    from.getFullYear(),
    from.getMonth(),
    from.getDate()
  ).getTime();
  const toMs = new Date(
    to.getFullYear(),
    to.getMonth(),
    to.getDate(),
    23,
    59,
    59,
    999
  ).getTime();

  useEffect(() => {
    if (!enabled) return undefined;
    setLoadedKinds({});
    setErrorsByKind({});
    const kinds = dateKindsKey.split(",");
    // 選択中の種類の前回データは残す(初回スナップショット到着までの表示つなぎ。
    // 範囲外の行は applyOrderSearch が弾く)が、選択から外れた種類のデータは捨てる。
    // 残すと、後でその種類を選び直したとき「前回購読時点の古い内容」が
    // 新しいスナップショットが届くまで混ざってしまう
    setDocsByKind((prev) => {
      const kept = {};
      kinds.forEach((kindId) => {
        if (prev[kindId]) {
          kept[kindId] = prev[kindId];
        }
      });
      return kept;
    });
    const unsubscribes = kinds.map((kindId) => {
      const field = dateKindToField(kindId);
      const ordersQuery = query(
        collection(db, "orders"),
        where(field, ">=", FirebaseTimestamp.fromMillis(fromMs)),
        where(field, "<=", FirebaseTimestamp.fromMillis(toMs))
      );
      return onSnapshot(
        ordersQuery,
        (snapshot) => {
          const docs = new Map();
          snapshot.docs.forEach((docSnapshot) => {
            docs.set(docSnapshot.id, docSnapshot.data());
          });
          setDocsByKind((prev) => ({ ...prev, [kindId]: docs }));
          setLoadedKinds((prev) => ({ ...prev, [kindId]: true }));
          // この種類のエラーは解消
          setErrorsByKind((prev) => {
            if (!prev[kindId]) return prev;
            const next = { ...prev };
            delete next[kindId];
            return next;
          });
        },
        (snapshotError) => {
          // 権限やネットワークの問題。画面側でインライン表示するため state に持つ
          console.error("Error listening to orders:", snapshotError);
          setErrorsByKind((prev) => ({ ...prev, [kindId]: snapshotError }));
        }
      );
    });
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [enabled, dateKindsKey, fromMs, toMs]);

  // 選択中の種類の結果を docId で union し(複数種類にマッチする明細の重複排除)、
  // クライアント側の絞り込みとソートを適用する
  const statusesKey = conditions.statuses.join(",");
  const departmentsKey =
    conditions.departmentIds === null
      ? "all"
      : conditions.departmentIds.join(",");
  const myOwn = conditions.myOwn;
  const orders = useMemo(() => {
    const merged = new Map();
    dateKindsKey.split(",").forEach((kindId) => {
      const docs = docsByKind[kindId];
      if (docs) {
        docs.forEach((data, id) => merged.set(id, data));
      }
    });
    return applyOrderSearch([...merged.values()], conditions, loginUserEmail);
    // conditions の意味的な変化はすべて下のプリミティブキーで捕捉している
    // (オブジェクト識別子に依存すると無関係な再レンダーで毎回再計算になる)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    docsByKind,
    dateKindsKey,
    fromMs,
    toMs,
    statusesKey,
    departmentsKey,
    myOwn,
    loginUserEmail,
  ]);

  // 購読開始前(enabled=false は URL/LS 復元前だけ)もローディング扱いにする
  // (初回描画で一瞬「該当 0 件」と表示されるのを防ぐ)
  const isLoading =
    !enabled || dateKindsKey.split(",").some((kindId) => !loadedKinds[kindId]);

  // 現在選択中の種類のエラーだけを見る(過去の選択で起きたエラーは無視)
  const error =
    dateKindsKey
      .split(",")
      .map((kindId) => errorsByKind[kindId])
      .find(Boolean) ?? null;

  return { orders, isLoading, error };
};

export default useOrdersSubscription;
